import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Tooltip } from "flowbite-react";
import { isUserOwnerOfWorkspace } from "../lib/utils";

const CheckIcon = () => (
  <svg
    className="w-6 h-6 text-white bg-blue-500 rounded-full p-1"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M5 13l4 4L19 7"
    ></path>
  </svg>
);

const ArrowDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    fill="currentColor"
    className="bi bi-chevron-down"
    viewBox="0 0 16 16"
  >
    <path
      fillRule="evenodd"
      d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
    />
  </svg>
);

const UpgradeModal = ({ isOpen, setIsOpen }) => {
  const [planType, setPlanType] = useState("subscription"); // 'subscription' or 'on_demand'
  const [billingCycle, setBillingCycle] = useState("monthly"); // 'monthly' or 'yearly'
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOwner = isUserOwnerOfWorkspace();
  const [selectedAmount, setSelectedAmount] = useState(10);
  const [inputAmount, setInputAmount] = useState(10);

  // Derive current subscription info from localStorage
  const currentSubscription = (() => {
    try {
      const loginData = JSON.parse(localStorage.getItem("login_data") || "{}");
      return loginData?.resp_user?.subscription || {};
    } catch {
      return {};
    }
  })();

  // Detect if the currently selected plan/tab matches the user's active plan
  const isCurrentPlan = (() => {
    if (planType === "on_demand") {
      return currentSubscription?.subscription_type === "usage_based";
    }
    // For subscription tab compare stripe_price_id
    if (selectedPlan?.stripe_price_id && currentSubscription?.stripe_price_id) {
      return selectedPlan.stripe_price_id === currentSubscription.stripe_price_id;
    }
    return false;
  })();

  // When isOpen is a number it represents the minimum required monthly price
  const minMonthlyPrice = typeof isOpen === "number" ? isOpen : null;

  const modalRef = useRef();

  const features = [
    "Everything in free, Plus",
    "More credits less cost",
    "Unlimited Seats",
    "User Activity & Traffic Insights",
    "Track Specified Pages",
    "ICP",
    "PIS",
    "Knowledge Base",
    "Auto Enrichment and Lead Nurturing",
    "Hands Off Hotleads",
    "Progressive Outbound Nurturing",
    "Hubspot Integration",
    "Salesforce Integration",
    "Apollo Integration",
    "Clay Integration",
    "Zoho CRM Integration",
    "Zapier Integration",
    "Integrate with other Lead Management products",
    "Access to APIs & Webhook"
  ];
  const no_features = [
    "Everything in free, Plus",
    "More credits less cost",
    "Unlimited Seats",
    "User Activity & Traffic Insights",
    "Track Specified Pages",
    "No ICP",
    "No PIS",
    "No Knowledge Base",
    "No Auto Enrichment and Lead Nurturing",
    "No Hands Off Hotleads",
    "No Progressive Outbound Nurturing",
    "Hubspot Integration",
    "Salesforce Integration",
    "Apollo Integration",
    "Clay Integration",
    "Zoho CRM Integration",
    "Zapier Integration",
    "Integrate with other Lead Management products",
    "Access to APIs & Webhook"
  ];

  useEffect(() => {
    if (!isOpen) return;

    const fetchPlans = async () => {
      setLoading(true);
      setError("");
      try {
        // Replace with your actual API endpoint
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_SIGNIN_URL}/subscription/kwin/plans`,
          {
            headers: {
              jwt_token: localStorage.getItem("login_token"),
            },
          }
        );

        // Transform the API response to the expected format
        // Sort by credits ascending
        const planprices =
          response?.data?.subscriptionplan?.planprices?.sort(
            (a, b) => a?.price_credits - b?.price_credits
          ) || [];
        const transformedPlans = {
          monthly: planprices.filter(
            (plan) => plan.subscription_type === "monthly"
          ),
          yearly: planprices.filter(
            (plan) => plan.subscription_type === "yearly"
          ),
        };

        setPlans(transformedPlans);
      } catch (err) {
        setError("Failed to load subscription plans. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [isOpen]);

  useEffect(() => {
    if (plans && plans[billingCycle]?.length > 0) {
      const cyclePlans = plans[billingCycle];
      if (minMonthlyPrice != null) {
        // Auto-select the plan whose equivalent monthly price matches minMonthlyPrice,
        // falling back to the first non-disabled plan above the threshold.
        const matchingPlan = cyclePlans.find((p) => {
          const effectivePrice =
            billingCycle === "yearly" ? p.price / 12 : p.price;
          return effectivePrice >= minMonthlyPrice;
        });
        setSelectedPlan(matchingPlan || cyclePlans[cyclePlans.length - 1]);
      } else {
        // Default behaviour: pick index 1 (or 0 if only one plan)
        setSelectedPlan(cyclePlans[1] || cyclePlans[0]);
      }
    } else {
      setSelectedPlan(null);
    }
  }, [plans, billingCycle, minMonthlyPrice]);
  const handleSelectChange = (e) => {
    const value = Number(e.target.value);
    setSelectedAmount(value);
    setInputAmount(value); // auto-fill input
  };

  const handleGetStarted = async () => {
    if (!selectedPlan || !selectedPlan.stripe_price_id) {
      setError("Please select a credit plan.");
      return;
    }
    setIsSubmitting(true);
    setError("");
    try {
      // Retrieve JWT token from storage (e.g., localStorage)
      const jwtToken = localStorage.getItem("login_token");
      if (!jwtToken) {
        setError("Authentication error. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      let payload = { price_id: selectedPlan.stripe_price_id };
      let paymenturl = `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/payment/create`;
      if (planType === "on_demand") {
        payload.mode_type = "setup";
        payload.price_id = null;
        paymenturl = `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/user/payment/asperchoice`;
        payload.amount = inputAmount * 100; // Pass the custom amount to the backend
      }

      const response = await axios.post(
        paymenturl,
        payload,
        {
          headers: {
            jwt_token: jwtToken,
          },
        }
      );

      localStorage.setItem("price_id", selectedPlan.stripe_price_id);

      if (response.data && response.data.redirect_url) {
        localStorage.setItem(
          "stripe_vision_session_id",
          response.data.session_id
        );
        window.location.href = response.data.redirect_url;
      } else {
        setError("Could not initiate payment. Please try again.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        "An error occurred during payment initiation."
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpen(false);
        localStorage.removeItem("open_upgrade");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [setIsOpen]);

  if (!isOpen) return null;

  const currentPlans = plans?.[billingCycle] || [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 ">
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl  shadow-2xl w-full max-w-[80vw] max-h-[90vh] flex flex-col border-4 border-purple-300 "
      >
        {/* Modal Header */}
        <div className="w-full flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50/10 rounded-t-2xl z-20">
          <div className="hidden md:block flex-1"></div> {/* Spacer for center alignment */}

          <div className="flex justify-center flex-1 md:flex-none">
            <div className="relative flex items-center bg-gray-100 rounded-full">
              <button
                onClick={() => setPlanType("subscription")}
                className={`px-6 py-2 text-sm font-semibold rounded-full transition-all duration-300 relative z-10 ${planType === "subscription" ? "text-white" : "text-gray-500 hover:text-gray-800"
                  }`}
              >
                Subscription
              </button>
              <button
                onClick={() => setPlanType("on_demand")}
                className={`px-6 py-2 text-sm font-semibold rounded-full transition-all duration-300 relative z-10 ${planType === "on_demand" ? "text-white" : "text-gray-500 hover:text-gray-800"
                  }`}
              >
                Pay-as-you-go
              </button>
              <div
                className={`absolute top-0 h-9 w-1/2 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-full shadow-md transform transition-transform duration-300 ${planType === "on_demand"
                  ? "translate-x-full"
                  : "translate-x-0"
                  }`}
              ></div>
            </div>
          </div>

          <div className="flex-1 flex justify-end">
            <button
              onClick={() => {
                setIsOpen(false);
                localStorage.removeItem("open_upgrade");
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors mr-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 flex-grow">
          {/* Left Section */}
          <div className="p-8 max-h-[calc(100vh-200px)]  overflow-auto  border-b md:border-b-0 md:border-r border-gray-200">
            <h2 className="text-3xl font-bold text-blue-900">
              {planType === "on_demand" ? "Pay-as-you-go" : "Win+"}
            </h2>
            {planType === "subscription" && (
              <p className="text-gray-500 mt-2">Save 25% with yearly billing</p>
            )}
            <ul className={`mt-6 space-y-3 `}>
              {/* if selected plans credits are 150 or 1800 */}
              {selectedPlan?.price_credits === 150 ||
                selectedPlan?.price_credits === 1800 || planType == "on_demand" ? (
                <>
                  {no_features.filter(item => {
                    // if plan type is on demand then exclude "More credits less cost"
                    if (planType == "on_demand") {
                      return item !== "More credits less cost";
                    }
                    return item;
                  }).map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckIcon />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </>
              ) : (
                <>
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckIcon />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </>
              )}
            </ul>
          </div>

          {/* Right Section */}
          <div className="p-8  max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden  relative flex flex-col justify-center">
            {/* <div className="absolute top-4 right-4">
            <span className="bg-purple-200 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                fill="currentColor"
                className="bi bi-fire"
                viewBox="0 0 16 16"
              >
                <path d="M8 16c3.314 0 6-2 6-5.5 0-1.5-.5-4-2.5-6 .25 1.5-1.25 2-1.25 2C11 4 9 .5 6 0c.357 2 .5 4-2 6-1.25 1-2 2.729-2 4.5C2 14 4.686 16 8 16m0-1c-1.657 0-3-1-3-2.75 0-.75.25-2 1.25-3C6.125 10 7 8.5 7 8.5c0-1 .5-1.5 1.5-2C10 5 11.5 6.5 11.5 7.5c0 1.25-1.25 2.5-1.25 2.5C10.25 11.5 10 12.25 10 13c0 1.5-1.343 2-2 2" />
              </svg>
              MOST POPULAR
            </span>
          </div> */}

            <div className={` flex flex-col`}>
              {/* Toggle */}
              {planType === "subscription" && (
                <div className="flex justify-center mb-6 mt-8 md:mt-0">
                  <div className="relative flex items-center bg-gray-100 rounded-full  ">
                    <button
                      onClick={() => setBillingCycle("monthly")}
                      className={`px-6 py-2 text-sm font-semibold rounded-full transition-all duration-300 relative z-10 ${billingCycle === "monthly" ? "text-white" : "text-gray-500"
                        }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingCycle("yearly")}
                      className={`px-6 py-2 text-sm font-semibold rounded-full transition-all duration-300 relative z-10 ${billingCycle === "yearly" ? "text-white" : "text-gray-500"
                        }`}
                    >
                      Yearly
                    </button>
                    <div
                      className={`absolute top-0  h-9 w-1/2 bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] rounded-full shadow-md transform transition-transform duration-300 ${billingCycle === "yearly"
                        ? "translate-x-full"
                        : "translate-x-0"
                        }`}
                    ></div>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center mt-8">Loading plans...</div>
              ) : error && !plans ? (
                <div className="text-center text-red-500 mt-8">{error}</div>
              ) : (
                <>
                  {planType === "subscription" && (
                    <>
                      <div className="text-center my-4">
                        <span className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                          $
                          {selectedPlan?.price
                            ? billingCycle == "yearly"
                              ? selectedPlan?.price / 12
                              : selectedPlan?.price
                            : "N/A"}
                        </span>
                        <span className="text-xl text-gray-700 font-medium ml-2">
                          per month
                        </span>
                      </div>
                      <p className="text-center text-sm text-gray-500">
                        {billingCycle === "yearly"
                          ? "Billed annually • Save 25%"
                          : "Billed monthly"}
                      </p>
                    </>
                  )}

                  {planType === "on_demand" && (
                    <div className="text-center my-4 mb-4">
                      <span className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                        Pay-as-you-go
                      </span>
                      <p className="text-center text-sm text-gray-500 max-w-xs mx-auto mt-4">
                        Scale effortlessly without a fixed monthly fee. Pay only for what you use.
                      </p>


                    </div>

                  )}

                  <p className={`text-center text-sm text-gray-500 ${planType === 'on_demand' ? 'text-2xl font-medium mb-8' : ''}`}>
                    Cost: $
                    {planType == 'subscription' ? selectedPlan?.price
                      ? (
                        selectedPlan?.price / selectedPlan?.price_credits
                      ).toFixed(2)
                      : "N/A"
                      : "0.50"}
                    {" "}per identified lead
                  </p>
                  {/* on Demand Dropdown */}
                  {planType === "on_demand" && (
                    <div className="my-4 relative">
                      <p>Choose an amount to buy credits</p>
                      <select
                        value={selectedAmount}
                        onChange={handleSelectChange}
                        className="w-full border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10"
                      >
                        <option value={10}>10 USD</option>
                        <option value={20}>20 USD</option>
                        <option value={30}>30 USD</option>
                        <option value={40}>40 USD</option>
                        <option value={50}>50 USD</option>
                        <option value={100}>100 USD</option>
                        <option value={150}>150 USD</option>
                        <option value={200}>200 USD</option>
                        <option value={300}>300 USD</option>
                      </select>

                    </div>

                  )}

                  {/* Credits Dropdown */}
                  {planType === "subscription" && (
                    <div className="my-6 relative">
                      <select
                        value={selectedPlan?.stripe_price_id || ""}
                        onChange={(e) => {
                          const plan = currentPlans.find(
                            (p) => p.stripe_price_id === e.target.value
                          );
                          setSelectedPlan(plan);
                        }}
                        className="w-full p-3 border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500 pr-10"
                      >
                        {currentPlans.length > 0 ? (
                          currentPlans.map((plan) => {
                            // Determine effective monthly price for this plan
                            const effectiveMonthlyPrice =
                              billingCycle === "yearly"
                                ? plan.price / 12
                                : plan.price;
                            const isDisabled =
                              minMonthlyPrice != null &&
                              effectiveMonthlyPrice < minMonthlyPrice;
                            return (
                              <option
                                key={plan.stripe_price_id}
                                value={plan.stripe_price_id}
                                disabled={isDisabled}
                                style={isDisabled ? { color: "#9ca3af" } : {}}
                              >
                                {plan.price_credits} Credits
                              </option>
                            );
                          })
                        ) : (
                          <option>No plans available</option>
                        )}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                        <ArrowDownIcon />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="">
              {error && plans && (
                <p className="text-red-500 text-sm text-center mb-2">{error}</p>
              )}
              {isCurrentPlan && (
                <p className="text-center text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-3">
                  ✓ You’re currently on this plan
                </p>
              )}
              {planType === "subscription" && currentSubscription?.subscription_type && currentSubscription?.subscription_type !== "usage_based" && currentSubscription?.subscription_type != "free" && (
                <p className="text-center text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-3">
                  You're currently on a subscription plan. Cancel this plan to switch to Pay-as-you-go.{" "}
                  <span className="block mt-1 text-amber-600">
                    Note: Knowledge Base, ICP, PIS, and Auto Email Outreach are not included in the Pay-as-you-go plan.
                  </span>
                </p>
              )}
              {planType === "on_demand" && currentSubscription?.subscription_type && currentSubscription?.subscription_type == "usage_based" && currentSubscription?.subscription_type != "free" && (
                <p className="text-center text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 mb-3">
                  You're currently on the Pay-as-you-go plan. To switch to a subscription, please cancel this plan and choose your desired subscription.
                </p>
              )}
              <div className="space-y-3">

                <div className="w-full [&>div]:w-full [&>div]:!block">
                  {isOwner ? (

                    <button
                      onClick={handleGetStarted}
                      disabled={isSubmitting || loading || !selectedPlan || isCurrentPlan}
                      className="w-full bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white font-bold py-3 px-4 rounded-lg hover:opacity-90 transition-opacity duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        planType === "subscription" ? "Get Started" : "Add Card"
                      )}
                    </button>
                  ) : (
                    <Tooltip
                      content={isCurrentPlan ? "You’re currently on this plan" : "Contact workspace owner to upgrade the workspace"}
                      placement="top"
                      style="light"
                    >
                      <button
                        disabled
                        className="w-full bg-gradient-to-r from-[#5E78F4] to-[#F14CE2] text-white font-bold py-3 px-4 rounded-lg  transition-opacity duration-300 opacity-50 cursor-not-allowed flex justify-center items-center"
                      >
                        {planType === "subscription" ? "Get Started" : "Add Card"}
                      </button>
                    </Tooltip>
                  )}
                </div>
                {/* <button
                onClick={() => {
                  setIsOpen(false);
                  localStorage.removeItem("open_upgrade");
                }}
                className="w-full bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors duration-300"
              >
                Cancel
              </button> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;